import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import type { Item, WorkOrder, WorkOrderDailyCostLineRequest, WorkOrderShiftEntryRequest } from '../../services/types';
import { Button } from '../../components/ui/button';
import EntityTypeaheadPicker, { type PickerOption } from '../../components/search/EntityTypeaheadPicker';
import { getOperatorWorkOrderLabel, getWorkOrderDescriptionSummary } from './workOrderDisplay';

type ShiftMaterialLine = {
    key: string;
    item_id: string;
    qty: string;
    remarks: string;
};

type DirectCostType = 'SUBCON' | 'TRANSPORT' | 'SERVICE' | 'DIRECT_LABOUR_HIRE' | 'OTHER';

const DIRECT_COST_OPTIONS: Array<{ value: DirectCostType; label: string }> = [
    { value: 'SUBCON', label: 'Subcontracting' },
    { value: 'TRANSPORT', label: 'Transport' },
    { value: 'SERVICE', label: 'Service' },
    { value: 'DIRECT_LABOUR_HIRE', label: 'Direct Labour Hire' },
    { value: 'OTHER', label: 'Other' },
];

type ShiftEntryRow = {
    key: string;
    selected: boolean;
    wo_id: string;
    wo_item_id: string;
    output_qty: string;
    output_weight_kgs: string;
    auto_backflush_bom: boolean;
    include_child_recipe_materials: boolean;
    labor_hours: string;
    hourly_rate: string;
    installation_labor_hours: string;
    installation_hourly_rate: string;
    overhead_cost: string;
    direct_cost_type: DirectCostType;
    direct_cost_amount: string;
    direct_cost_remarks: string;
    remarks: string;
    materials: ShiftMaterialLine[];
};

type DailyShiftEntryProps = {
    orders: WorkOrder[];
    items: Item[];
    onPosted: () => void | Promise<void>;
};

const newKey = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const emptyRow = (): ShiftEntryRow => ({
    key: newKey(),
    selected: true,
    wo_id: '',
    wo_item_id: '',
    output_qty: '0',
    output_weight_kgs: '0',
    auto_backflush_bom: false,
    include_child_recipe_materials: false,
    labor_hours: '0',
    hourly_rate: '0',
    installation_labor_hours: '0',
    installation_hourly_rate: '0',
    overhead_cost: '0',
    direct_cost_type: 'SUBCON',
    direct_cost_amount: '0',
    direct_cost_remarks: '',
    remarks: '',
    materials: [],
});

const isStockMaterial = (item: Item): boolean => {
    const role = String(item.item_role || '').toUpperCase();
    const type = String(item.item_type || 'Stock').trim().toLowerCase().replace(/[-_\s]/g, '');
    return type === 'stock' && !['FG', 'SERVICE', 'CONSUMABLE', 'NON_STOCK', 'NONSTOCK', 'ASSET', 'FIXED_ASSET'].includes(role);
};

const num = (value: string) => {
    const parsed = Number(value || '0');
    return Number.isFinite(parsed) ? parsed : 0;
};

export function DailyShiftEntry({ orders, items, onPosted }: DailyShiftEntryProps) {
    const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [shiftCode, setShiftCode] = useState('');
    const [headerRemarks, setHeaderRemarks] = useState('');
    const [rows, setRows] = useState<ShiftEntryRow[]>([emptyRow()]);
    const [busy, setBusy] = useState(false);
    const [busyRowKey, setBusyRowKey] = useState<string | null>(null);
    const [bulkMaterialId, setBulkMaterialId] = useState('');
    const [bulkMaterialQty, setBulkMaterialQty] = useState('0');
    const [bulkMaterialRemarks, setBulkMaterialRemarks] = useState('');
    const [bulkLaborHours, setBulkLaborHours] = useState('0');
    const [bulkLaborRate, setBulkLaborRate] = useState('0');
    const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

    const openOrders = useMemo(
        () => orders.filter((order) => !['completed', 'cancelled'].includes(String(order.status || '').toLowerCase().replace(/\s|-/g, ''))),
        [orders],
    );

    const orderOptions = useMemo<PickerOption<string>[]>(() => (
        openOrders.map((order) => ({
            value: String(order.wo_id),
            label: getOperatorWorkOrderLabel(order),
            description: getWorkOrderDescriptionSummary(order),
            badges: [
                { label: order.status || 'Draft', tone: String(order.status || '').toLowerCase().includes('progress') ? 'blue' : 'slate' },
                { label: `Qty ${Number(order.quantity || 0).toFixed(2)}`, tone: 'slate' },
            ],
            colorToken: String(order.status || '').toLowerCase().includes('progress') ? 'wo-in-progress' : 'wo-planned',
        }))
    ), [openOrders]);

    const orderById = useMemo(() => {
        const map = new Map<number, WorkOrder>();
        openOrders.forEach((order) => map.set(Number(order.wo_id), order));
        return map;
    }, [openOrders]);

    const materialOptions = useMemo<PickerOption<string>[]>(() => (
        items.filter(isStockMaterial).map((item) => ({
            value: String(item.id),
            label: `${item.code} - ${item.name}`,
            description: item.category || 'Material',
            badges: [
                { label: item.uom || '-', tone: 'slate' },
                { label: `Stock ${Number(item.current_stock || 0).toFixed(3)}`, tone: Number(item.current_stock || 0) > 0 ? 'green' : 'amber' },
            ],
        }))
    ), [items]);

    const selectedRowKeys = useMemo(() => rows.filter((row) => row.selected).map((row) => row.key), [rows]);

    const updateRow = (key: string, patch: Partial<ShiftEntryRow>) => {
        setRows((prev) => prev.map((row) => row.key === key ? { ...row, ...patch } : row));
        setRowErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const addMaterialLine = (rowKey: string) => {
        setRows((prev) => prev.map((row) => row.key === rowKey ? {
            ...row,
            materials: [...row.materials, { key: newKey(), item_id: '', qty: '0', remarks: '' }],
        } : row));
    };

    const updateMaterialLine = (rowKey: string, lineKey: string, patch: Partial<ShiftMaterialLine>) => {
        setRows((prev) => prev.map((row) => row.key === rowKey ? {
            ...row,
            materials: row.materials.map((line) => line.key === lineKey ? { ...line, ...patch } : line),
        } : row));
    };

    const removeMaterialLine = (rowKey: string, lineKey: string) => {
        setRows((prev) => prev.map((row) => row.key === rowKey ? {
            ...row,
            materials: row.materials.filter((line) => line.key !== lineKey),
        } : row));
    };

    const distributeMaterial = () => {
        const selectedCount = selectedRowKeys.length;
        const totalQty = num(bulkMaterialQty);
        if (!bulkMaterialId || selectedCount === 0 || totalQty <= 0) {
            toast.error('Select material, rows, and total quantity first');
            return;
        }
        const qtyEach = totalQty / selectedCount;
        setRows((prev) => prev.map((row) => {
            if (!row.selected) return row;
            return {
                ...row,
                materials: [
                    ...row.materials,
                    {
                        key: newKey(),
                        item_id: bulkMaterialId,
                        qty: qtyEach.toFixed(3),
                        remarks: bulkMaterialRemarks || '',
                    },
                ],
            };
        }));
        toast.success(`Material distributed across ${selectedCount} WO row(s)`);
    };

    const distributeLabor = () => {
        const selectedCount = selectedRowKeys.length;
        const totalHours = num(bulkLaborHours);
        if (selectedCount === 0 || totalHours <= 0) {
            toast.error('Select rows and enter total manhours first');
            return;
        }
        const hoursEach = totalHours / selectedCount;
        setRows((prev) => prev.map((row) => row.selected ? {
            ...row,
            labor_hours: hoursEach.toFixed(2),
            hourly_rate: bulkLaborRate,
        } : row));
        toast.success(`Manhours distributed across ${selectedCount} WO row(s)`);
    };

    const buildPayload = (sourceRows: ShiftEntryRow[] = rows): WorkOrderShiftEntryRequest | null => {
        const nextErrors: Record<string, string> = {};
        const payloadRows = sourceRows.map((row) => {
            const order = orderById.get(Number(row.wo_id || 0));
            if (!order) {
                nextErrors[row.key] = 'Select a work order.';
            }
            const output = num(row.output_qty);
            const weight = num(row.output_weight_kgs);
            if ((output > 0 || weight > 0) && !row.wo_item_id) {
                nextErrors[row.key] = 'Select a part line when posting output or weight.';
            }
            const issueLines = row.materials
                .map((line) => ({
                    item_id: Number(line.item_id || 0),
                    qty: num(line.qty),
                    row_type: 'UNPLANNED' as const,
                    remarks: line.remarks.trim() || row.remarks.trim() || undefined,
                }))
                .filter((line) => line.item_id > 0 && line.qty > 0);
            const costLines: WorkOrderDailyCostLineRequest[] = num(row.direct_cost_amount) > 0 ? [{
                cost_type: row.direct_cost_type,
                amount: num(row.direct_cost_amount),
                remarks: row.direct_cost_remarks.trim() || undefined,
            }] : [];
            return {
                client_row_id: row.key,
                wo_id: Number(row.wo_id || 0),
                wo_item_id: row.wo_item_id ? Number(row.wo_item_id) : undefined,
                output_qty: output,
                output_weight_kgs: weight,
                auto_backflush_bom: row.auto_backflush_bom,
                include_child_recipe_materials: row.include_child_recipe_materials,
                issue_lines: [],
                consume_lines: issueLines,
                labor_hours: num(row.labor_hours),
                hourly_rate: num(row.hourly_rate),
                installation_labor_hours: num(row.installation_labor_hours),
                installation_hourly_rate: num(row.installation_hourly_rate),
                overhead_cost: num(row.overhead_cost),
                cost_lines: costLines,
                remarks: row.remarks.trim() || undefined,
            };
        });
        if (Object.keys(nextErrors).length > 0) {
            setRowErrors(nextErrors);
            return null;
        }
        return {
            entry_date: entryDate,
            shift_code: shiftCode.trim() || undefined,
            remarks: headerRemarks.trim() || undefined,
            rows: payloadRows,
        };
    };

    const submit = async () => {
        const payload = buildPayload();
        if (!payload) {
            toast.error('Fix highlighted rows before posting');
            return;
        }
        try {
            setBusy(true);
            setRowErrors({});
            const result = await api.manufacturing.postShiftEntry(payload);
            toast.success(`Posted ${result.posted_count} shift row(s)`);
            await onPosted();
            setRows([emptyRow()]);
            setHeaderRemarks('');
        } catch (err: any) {
            const errors = err?.detail?.row_errors || [];
            if (Array.isArray(errors) && errors.length) {
                const next: Record<string, string> = {};
                errors.forEach((error: any) => {
                    if (error.client_row_id) {
                        next[String(error.client_row_id)] = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
                    }
                });
                setRowErrors(next);
            }
            toast.error(err?.message || 'Failed to post shift entry');
        } finally {
            setBusy(false);
        }
    };

    const postRow = async (row: ShiftEntryRow) => {
        const payload = buildPayload([row]);
        if (!payload) {
            toast.error('Fix highlighted row before posting');
            return;
        }
        try {
            setBusyRowKey(row.key);
            setRowErrors({});
            const result = await api.manufacturing.postShiftEntry(payload);
            toast.success(`Posted ${result.posted_count} shift row(s)`);
            await onPosted();
            setRows((prev) => {
                if (prev.length <= 1) return [emptyRow()];
                return prev.filter((candidate) => candidate.key !== row.key);
            });
        } catch (err: any) {
            const errors = err?.detail?.row_errors || [];
            if (Array.isArray(errors) && errors.length) {
                const next: Record<string, string> = {};
                errors.forEach((error: any) => {
                    if (error.client_row_id) {
                        next[String(error.client_row_id)] = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
                    }
                });
                setRowErrors(next);
            }
            toast.error(err?.message || 'Failed to post shift row');
        } finally {
            setBusyRowKey(null);
        }
    };

    const renderSectionTitle = (title: string, description?: string) => (
        <div className="border-b pb-2">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            {description ? <div className="text-xs text-slate-500">{description}</div> : null}
        </div>
    );

    return (
        <div className="space-y-4 pb-8">
            <div>
                <h2 className="text-2xl font-bold text-slate-900">Daily Shift Entry</h2>
                <p className="text-sm text-slate-500">Use this screen to post material, labour, installation, finished output, and direct costs across multiple WOs for one date/shift.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 rounded-lg border bg-white p-4">
                <div>
                    <label className="text-sm font-medium">Date</label>
                    <input type="date" className="mt-1 w-full rounded border p-2" value={entryDate} onChange={(event) => setEntryDate(event.target.value)} />
                </div>
                <div>
                    <label className="text-sm font-medium">Shift</label>
                    <input className="mt-1 w-full rounded border p-2" value={shiftCode} onChange={(event) => setShiftCode(event.target.value)} placeholder="Day / Night / A" />
                </div>
                <div className="md:col-span-2">
                    <label className="text-sm font-medium">Shift Remarks</label>
                    <input className="mt-1 w-full rounded border p-2" value={headerRemarks} onChange={(event) => setHeaderRemarks(event.target.value)} placeholder="Optional common note for all rows" />
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 rounded-lg border bg-slate-50 p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                    <div className="md:col-span-2">
                        <label className="text-sm font-medium">Bulk Material</label>
                        <EntityTypeaheadPicker value={bulkMaterialId} options={materialOptions} onChange={setBulkMaterialId} placeholder="Search material..." />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Total Qty</label>
                        <input type="number" min="0" step="0.001" className="mt-1 w-full rounded border p-2" value={bulkMaterialQty} onChange={(event) => setBulkMaterialQty(event.target.value)} />
                    </div>
                    <Button type="button" onClick={distributeMaterial} variant="outline">Distribute Material</Button>
                    <div className="md:col-span-4">
                        <input className="w-full rounded border p-2 text-sm" value={bulkMaterialRemarks} onChange={(event) => setBulkMaterialRemarks(event.target.value)} placeholder="Optional material note" />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                    <div>
                        <label className="text-sm font-medium">Total Manhours</label>
                        <input type="number" min="0" step="0.01" className="mt-1 w-full rounded border p-2" value={bulkLaborHours} onChange={(event) => setBulkLaborHours(event.target.value)} />
                    </div>
                    <div>
                        <label className="text-sm font-medium">Rate</label>
                        <input type="number" min="0" step="0.01" className="mt-1 w-full rounded border p-2" value={bulkLaborRate} onChange={(event) => setBulkLaborRate(event.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                        <Button type="button" onClick={distributeLabor} variant="outline" className="w-full">Distribute Manhours</Button>
                    </div>
                    <div className="md:col-span-4 text-xs text-slate-500">
                        Distribution applies equally to selected rows. Operators can still fine-tune each WO row before posting.
                    </div>
                </div>
            </div>

            <div className="space-y-3">
	                {rows.map((row, index) => {
	                    const order = orderById.get(Number(row.wo_id || 0));
	                    const partOptions: PickerOption<string>[] = (order?.items || []).map((item) => ({
	                        value: String(item.wo_item_id),
                        label: `${item.description || `Line ${item.wo_item_id}`}${item.sub_part_no ? ` [${item.sub_part_no}]` : ''}`,
                        description: `Remaining ${Math.max(0, Number(item.quantity || 0) - Number(item.completed_qty || 0)).toFixed(3)} ${item.uom || ''}`,
	                        badges: [{ label: item.line_role || 'LINE', tone: item.parent_wo_item_id ? 'blue' : 'slate' }],
	                    }));
	                    const selectedPart = (order?.items || []).find((item) => String(item.wo_item_id) === String(row.wo_item_id));
	                    const rowHasRecipe = Number(selectedPart?.standard_recipe_bom_id || order?.bom_id || 0) > 0;
	                    const rowIsWeightBased = String(selectedPart?.production_mode || '').toUpperCase() === 'WEIGHT_BASED';
	                    const rowCanAutoRecipe = rowHasRecipe && !rowIsWeightBased;
	                    return (
	                        <div key={row.key} className="rounded-lg border bg-white p-4 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <label className="flex items-center gap-2 text-sm font-semibold">
                                    <input type="checkbox" checked={row.selected} onChange={(event) => updateRow(row.key, { selected: event.target.checked })} />
                                    Row {index + 1}
                                </label>
                                <Button type="button" variant="ghost" size="sm" onClick={() => setRows((prev) => prev.length <= 1 ? prev : prev.filter((candidate) => candidate.key !== row.key))}>
                                    <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                            </div>

                            {rowErrors[row.key] ? (
                                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{rowErrors[row.key]}</div>
                            ) : null}

	                            <div className="rounded-md border p-3 space-y-3">
	                                {renderSectionTitle('Work Done', 'Select the WO line and enter the output or weight completed this shift.')}
	                            <div className="grid grid-cols-1 lg:grid-cols-6 gap-3">
                                <div className="lg:col-span-2">
                                    <label className="text-sm font-medium">WO Number</label>
                                    <EntityTypeaheadPicker
                                        value={row.wo_id}
                                        options={orderOptions}
                                        onChange={(value) => updateRow(row.key, { wo_id: value, wo_item_id: '' })}
                                        placeholder="Search WO / project..."
                                    />
                                </div>
                                <div className="lg:col-span-2">
                                    <label className="text-sm font-medium">Part Line</label>
                                    <EntityTypeaheadPicker
                                        value={row.wo_item_id}
                                        options={partOptions}
                                        onChange={(value) => updateRow(row.key, { wo_item_id: value })}
                                        placeholder="Search part / sub-part..."
                                        disabled={!order}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Output Qty</label>
                                    <input type="number" min="0" step="0.001" className="mt-1 w-full rounded border p-2" value={row.output_qty} onChange={(event) => updateRow(row.key, { output_qty: event.target.value })} />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Weight kg</label>
                                    <input type="number" min="0" step="0.001" className="mt-1 w-full rounded border p-2" value={row.output_weight_kgs} onChange={(event) => updateRow(row.key, { output_weight_kgs: event.target.value })} />
                                </div>
                            </div>

	                            {rowCanAutoRecipe ? (
	                            <div className="flex flex-wrap gap-2">
	                                <label className="inline-flex items-start gap-2 rounded border bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                                    <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={row.auto_backflush_bom}
                                        onChange={(event) => updateRow(row.key, {
                                            auto_backflush_bom: event.target.checked,
                                            include_child_recipe_materials: event.target.checked ? row.include_child_recipe_materials : false,
                                        })}
                                    />
	                                    <span>
	                                        <span className="font-medium">Use recipe materials automatically</span>
	                                        <span className="block text-xs text-emerald-700">Recipe materials will be consumed with this output. Manual materials below are optional.</span>
	                                    </span>
	                                </label>
	                                {row.auto_backflush_bom && String(selectedPart?.line_role || '').toUpperCase() !== 'CHILD' ? (
	                                <label className="inline-flex items-start gap-2 rounded border bg-blue-50 px-3 py-2 text-sm text-blue-900">
                                    <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={row.include_child_recipe_materials}
                                        disabled={!row.auto_backflush_bom}
                                        onChange={(event) => updateRow(row.key, { include_child_recipe_materials: event.target.checked })}
                                    />
	                                    <span>
	                                        <span className="font-medium">Include sub-part materials</span>
	                                        <span className="block text-xs text-blue-700">Use only when the parent output should consume child recipe materials together.</span>
	                                    </span>
	                                </label>
	                                ) : null}
	                            </div>
	                            ) : null}
	                            </div>

	                            <div className="rounded-md border p-3 space-y-3">
	                                {renderSectionTitle('Time', 'Record production and installation hours separately.')}
	                            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                                <NumericInput label="Prod Hrs" value={row.labor_hours} onChange={(value) => updateRow(row.key, { labor_hours: value })} />
                                <NumericInput label="Prod Rate" value={row.hourly_rate} onChange={(value) => updateRow(row.key, { hourly_rate: value })} />
                                <NumericInput label="Install Hrs" value={row.installation_labor_hours} onChange={(value) => updateRow(row.key, { installation_labor_hours: value })} />
                                <NumericInput label="Install Rate" value={row.installation_hourly_rate} onChange={(value) => updateRow(row.key, { installation_hourly_rate: value })} />
                                <NumericInput label="Overhead" value={row.overhead_cost} onChange={(value) => updateRow(row.key, { overhead_cost: value })} />
                                <div>
	                                    <label className="text-sm font-medium">Row Notes</label>
	                                    <input className="mt-1 w-full rounded border p-2" value={row.remarks} onChange={(event) => updateRow(row.key, { remarks: event.target.value })} />
	                                </div>
	                            </div>
	                            </div>

	                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 rounded border bg-slate-50 p-3">
	                                <div className="md:col-span-4">
	                                    {renderSectionTitle('Direct Costs', 'Direct job costs affect production costing only. Supplier bills/payables must still be entered through finance.')}
	                                </div>
	                                <div>
                                    <label className="text-sm font-medium">Direct Cost</label>
                                    <select className="mt-1 w-full rounded border p-2" value={row.direct_cost_type} onChange={(event) => updateRow(row.key, { direct_cost_type: event.target.value as ShiftEntryRow['direct_cost_type'] })}>
                                        {DIRECT_COST_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <NumericInput label="Amount" value={row.direct_cost_amount} onChange={(value) => updateRow(row.key, { direct_cost_amount: value })} />
                                <div className="md:col-span-2">
                                    <label className="text-sm font-medium">Direct Cost Remarks</label>
                                    <input className="mt-1 w-full rounded border p-2" value={row.direct_cost_remarks} onChange={(event) => updateRow(row.key, { direct_cost_remarks: event.target.value })} />
                                </div>
                            </div>

	                            <div className="space-y-2 rounded border p-3">
	                                <div className="flex items-center justify-between">
	                                    <div>
	                                        <div className="text-sm font-semibold">Materials Used</div>
	                                        <div className="text-xs text-slate-500">Manual material notes are optional.</div>
	                                    </div>
                                    <Button type="button" size="sm" variant="outline" onClick={() => addMaterialLine(row.key)}>
                                        <Plus className="h-4 w-4 mr-1" /> Add Material
                                    </Button>
                                </div>
                                {row.materials.length === 0 ? (
	                                    <div className="text-xs text-slate-500">No manual materials added. For weight-based GRC, enter output weight and the system will consume the configured mix.</div>
                                ) : null}
                                {row.materials.map((line) => (
                                    <div key={line.key} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                                        <div className="md:col-span-5">
                                            <label className="text-xs text-slate-500">Material</label>
                                            <EntityTypeaheadPicker value={line.item_id} options={materialOptions} onChange={(value) => updateMaterialLine(row.key, line.key, { item_id: value })} placeholder="Search material..." />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-xs text-slate-500">Qty</label>
                                            <input type="number" min="0" step="0.001" className="mt-1 w-full rounded border p-2" value={line.qty} onChange={(event) => updateMaterialLine(row.key, line.key, { qty: event.target.value })} />
                                        </div>
                                        <div className="md:col-span-4">
	                                            <label className="text-xs text-slate-500">Note (optional)</label>
	                                            <input className="mt-1 w-full rounded border p-2" value={line.remarks} onChange={(event) => updateMaterialLine(row.key, line.key, { remarks: event.target.value })} placeholder="Optional material note" />
                                        </div>
                                        <Button type="button" variant="ghost" onClick={() => removeMaterialLine(row.key, line.key)}>
                                            <Trash2 className="h-4 w-4 text-red-600" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end">
                                <Button type="button" size="sm" onClick={() => postRow(row)} disabled={busy || busyRowKey === row.key}>
                                    {busyRowKey === row.key ? 'Posting Row...' : 'Post Row'}
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex flex-wrap justify-between gap-2">
                <Button type="button" variant="outline" onClick={() => setRows((prev) => [...prev, emptyRow()])}>
                    <Plus className="h-4 w-4 mr-1" /> Add WO Row
                </Button>
                <Button type="button" onClick={submit} disabled={busy}>
                    {busy ? 'Posting...' : 'Post Shift Entry'}
                </Button>
            </div>
        </div>
    );
}

function NumericInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
    return (
        <div>
            <label className="text-sm font-medium">{label}</label>
            <input type="number" min="0" step="0.01" className="mt-1 w-full rounded border p-2" value={value} onChange={(event) => onChange(event.target.value)} />
        </div>
    );
}
